/**
 * Friendly error translation layer.
 *
 * The backend + Meta/Razorpay/TikTok APIs return either a machine code
 * (e.g. "WHATSAPP_WINDOW_EXPIRED") or a raw English sentence that is often
 * confusing to a non-technical user ("(#2534022) This message is sent outside
 * of allowed window"). This module turns those into short, friendly copy.
 *
 * Resolution order (see resolveFriendlyError):
 *   1. meta.code   -> CATALOG
 *   2. code        -> CATALOG
 *   3. raw message -> PATTERN matchers (for code-less Meta/network errors)
 *   4. code        -> generic humanizer (TITLE_CASE -> "Title case")
 *   5. raw message -> returned as-is (last resort, never worse than today)
 *
 * Adding a new entry: drop a row into CATALOG keyed by the backend `code`.
 * That's it — every call site that uses showApiError picks it up automatically.
 */

import { type Audience } from "@/lib/auth/currentUser";

/** How serious the error is — drives UI emphasis + analytics. */
export type ErrorSeverity = "info" | "warning" | "error" | "critical";

/** Coarse bucket for analytics dashboards + filtering. */
export type ErrorCategory =
  | "authentication"
  | "permission"
  | "integration"
  | "campaign"
  | "messaging"
  | "billing"
  | "validation"
  | "media"
  | "network"
  | "rate_limit"
  | "not_found"
  | "server"
  | "unknown";

export interface FriendlyError {
  /** Short toast title. */
  title: string;
  /** One-sentence, user-facing explanation + (where possible) the next step. */
  description: string;
  /** Severity for UI emphasis / analytics. Defaults to "error" when omitted. */
  severity?: ErrorSeverity;
  /** Whether retrying the same action might succeed. Defaults to false when omitted. */
  retryable?: boolean;
  /** Coarse analytics bucket. Inferred when omitted. */
  category?: ErrorCategory;
  /**
   * Who can actually resolve this — drives role-aware copy (Stripe/Shopify pattern):
   *   "admin"   -> needs owner/super-admin/admin. Privileged viewers see `action`
   *                (a CTA); agents see `whenCannotFix` instead and NO CTA.
   *   "support" -> platform-side, nobody in the org can fix it; everyone sees the
   *                authored "contact support" copy (no role swap).
   *   "anyone"  -> the viewer can fix it themselves (e.g. wrong input). No swap.
   * Omitted => no role-aware behavior (default, fully backward-compatible).
   */
  fixableBy?: "admin" | "support" | "anyone";
  /** CTA shown only to viewers who CAN fix it (privileged). e.g. { label: "Connect", to: "/home/integrations" } */
  action?: { label: string; to: string };
  /** Description shown to viewers who CANNOT fix it (agents) — replaces `description`. */
  whenCannotFix?: string;
}

/** The shape every apiClient call resolves to (subset we read here). */
export interface ApiErrorLike {
  error?: string | null;
  code?: string;
  message?: string;
  details?: string;
  meta?: { code?: string; subcode?: number | string; [k: string]: any } | null;
  statusCode?: number;
}

/**
 * Curated copy for the codes users hit most or find most confusing.
 * Keyed by the exact backend `code` / `meta.code` string.
 */
const CATALOG: Record<string, FriendlyError> = {
  // ---- WhatsApp / messaging ----
  WHATSAPP_WINDOW_EXPIRED: {
    title: "Reply window closed",
    description:
      "This chat's 24-hour reply window has closed. Send an approved template message to re-open the conversation.",
  },
  WHATSAPP_NOT_CONNECTED: {
    title: "WhatsApp not connected",
    description:
      "This business account isn't connected to WhatsApp yet. Finish connecting your WhatsApp Business account before creating a campaign.",
    fixableBy: "admin",
    action: { label: "Connect WhatsApp", to: "/home/business-accounts" },
    whenCannotFix:
      "This business account isn't connected to WhatsApp yet. Ask an admin on your team to finish connecting it before creating a campaign.",
  },
  BUSINESS_ACCOUNT_UNAVAILABLE: {
    title: "Business account unavailable",
    description:
      "This business account is no longer available. Select a connected WhatsApp Business account and try again.",
    fixableBy: "admin",
    action: { label: "Manage accounts", to: "/home/business-accounts" },
    whenCannotFix:
      "This business account is no longer available. Ask an admin on your team to connect a WhatsApp Business account.",
  },
  CONTACT_BLOCKED: {
    title: "Contact is blocked",
    description:
      "This contact is blocked, so messages and actions are paused. Unblock them first to continue.",
  },
  TEMPLATE_NOT_ON_META: {
    title: "Template not approved yet",
    description:
      "This template isn't live on Meta yet. Wait for Meta to approve it, or pick an approved template.",
  },
  TEMPLATE_NOT_FOUND: {
    title: "Template not found",
    description: "We couldn't find that template. It may have been deleted or renamed.",
  },
  TEMPLATE_STALE: {
    title: "Template changed",
    description:
      "This template was updated somewhere else. Refresh the page to load the latest version before saving.",
  },
  TEMPLATE_CATALOG_UNAVAILABLE: {
    title: "Templates unavailable",
    description: "The template catalog couldn't load right now. Please try again in a moment.",
  },
  TEMPLATE_INCOMPLETE: {
    title: "Template is incomplete",
    description:
      "A required part of this template is missing or empty. Fill in the header, body, and button text, then try again.",
  },
  PRODUCT_NOT_IN_CATALOG: {
    title: "Product not in your catalog",
    description:
      "This product isn't published to your Meta catalog yet. Sync your catalog in Commerce (or pick a product that's live), then try again.",
  },
  CATALOG_NOT_ACCESSIBLE: {
    title: "Catalog not accessible",
    description:
      "WhatsApp can't access this catalog with the connected account. Make sure the catalog belongs to the same Meta Business as this WhatsApp number and that the connected user has access to it in Meta Business Settings, then try again.",
    fixableBy: "admin",
    whenCannotFix:
      "WhatsApp can't access this catalog with the connected account. Ask an admin on your team to verify the catalog belongs to the same Meta Business and grant access in Meta Business Settings.",
  },

  // ---- Auth / permissions ----
  ROLE_FORBIDDEN: {
    title: "Not allowed",
    description:
      "You don't have permission to do this. Ask an admin on your team if you need access.",
  },
  UNAUTHORIZED: {
    title: "Please sign in again",
    description: "Your session looks invalid. Sign in again to continue.",
  },
  FORBIDDEN: {
    title: "Not allowed",
    description: "You don't have permission to do this. Ask an admin if you need access.",
  },

  // ---- Connections / reconnect ----
  NEEDS_RECONNECT: {
    title: "Reconnect required",
    description:
      "This account's connection expired. Reconnect it in Integrations to keep using it.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix:
      "This account's connection expired. Ask an admin on your team to reconnect it in Integrations.",
  },
  ACCOUNT_ORG_MISMATCH: {
    title: "Account not in this workspace",
    description:
      "That account belongs to a different workspace and can't be used here.",
  },
  ACCOUNT_NOT_FOUND: {
    title: "Account not found",
    description: "We couldn't find that connected account. It may have been disconnected.",
  },
  DEV_TOKEN_NOT_APPROVED: {
    title: "Access pending approval",
    description:
      "This integration is still pending approval from the provider and can't run live actions yet.",
  },
  CSE_NOT_CONFIGURED: {
    title: "Search not available yet",
    description: "This feature isn't enabled for your workspace yet. Ask your administrator to turn it on.",
  },
  AI_NOT_CONFIGURED: {
    title: "AI not set up",
    description: "AI isn't set up for this workspace yet. Add the AI settings in Settings to continue.",
    fixableBy: "admin",
    action: { label: "Open Settings", to: "/home/settings" },
    whenCannotFix: "AI isn't set up for this workspace yet. Ask your administrator to add the AI settings.",
  },
  NO_AD_ACCOUNT: {
    title: "No ad account connected",
    description: "Connect an ad account first to use this feature.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix: "No ad account is connected. Ask an admin on your team to connect one first.",
  },
  NO_AI_PHONE_NUMBER: {
    title: "No AI phone number",
    description: "Set up an AI phone number before starting calls.",
  },

  // ---- Rate limits / capacity ----
  RATE_LIMITED: {
    title: "Slow down a moment",
    description: "You've hit the rate limit. Wait a little while and try again.",
  },
  QUOTA_EXCEEDED: {
    title: "Daily limit reached",
    description: "You've used up the available quota for now. Try again later.",
  },
  QUEUE_UNAVAILABLE: {
    title: "Service busy",
    description: "The processing queue is unavailable right now. Please try again shortly.",
  },

  // ---- Resource conflicts ----
  FLOW_IN_USE: {
    title: "Flow is in use",
    description: "This flow is currently active and can't be changed. Pause it first.",
  },
  STATUS_IN_USE: {
    title: "Status in use",
    description: "This status is assigned to records and can't be deleted until they're moved.",
  },
  SITE_SLUG_CONFLICT: {
    title: "Address already taken",
    description: "That website address is already used. Pick a different one.",
  },

  // ---- Payments / amounts (payment links, plans, orders) ----
  INVALID_AMOUNT: {
    title: "Invalid amount",
    description: "Please enter a valid amount greater than 0.",
  },
  AMOUNT_INVALID_DECIMALS: {
    title: "Too many decimal places",
    description: "Amount can have at most 2 decimal places (e.g. 22.34).",
  },
  AMOUNT_TOO_LARGE: {
    title: "Amount too large",
    description: "This amount is too large to process. Please enter a smaller amount.",
  },

  // ---- Org payment gateways NOT connected (the merchant's OWN gateway, used for
  // payment links / collect-payment / refunds). Admin-fixable with a CTA to the
  // specific integration page; agents are told to ask their admin. ----
  RAZORPAY_NOT_CONNECTED: {
    title: "Razorpay not connected",
    description: "Connect Razorpay to start accepting payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect Razorpay", to: "/home/razorpay" },
    whenCannotFix: "Payments aren't set up yet. Ask an admin on your team to connect Razorpay.",
  },
  PAYU_NOT_CONNECTED: {
    title: "PayU not connected",
    description: "Connect PayU to start accepting payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect PayU", to: "/home/payu" },
    whenCannotFix: "Payments aren't set up yet. Ask an admin on your team to connect PayU.",
  },
  CASHFREE_NOT_CONNECTED: {
    title: "Cashfree not connected",
    description: "Connect Cashfree to start accepting payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Connect Cashfree", to: "/home/cashfree" },
    whenCannotFix: "Payments aren't set up yet. Ask an admin on your team to connect Cashfree.",
  },
  PAYMENT_NOT_CONNECTED: {
    title: "No payment method connected",
    description: "Connect a payment provider (Razorpay, PayU or Cashfree) to accept payments.",
    category: "billing",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix: "Payments aren't set up yet. Ask an admin on your team to connect a payment provider.",
  },

  // ---- Subscriptions / plan checkout ----
  PAYMENT_PLAN_NOT_CONFIGURED: {
    title: "This plan isn't ready for checkout",
    description: "It isn't fully set up yet. Please contact support to enable it.",
    category: "billing",
  },
  PAYMENT_GATEWAY_NOT_CONFIGURED: {
    title: "Payments aren't set up yet",
    description: "Please contact support to enable payments for your account.",
    category: "billing",
  },
  PAYMENT_GATEWAY_ERROR: {
    title: "Payments are temporarily unavailable",
    description: "We couldn't connect to our payment service at the moment. Please try again in a few minutes, or contact support if the issue continues.",
    category: "billing",
    retryable: true,
    fixableBy: "support",
  },
  PLAN_NOT_FOUND: {
    title: "We couldn't find that plan",
    description: "Refresh the page and pick a plan again.",
    category: "billing",
  },
  SUBSCRIPTION_ACTION_FAILED: {
    title: "We couldn't update your plan",
    description: "Please try again in a moment, or contact support if it continues.",
    category: "billing",
    retryable: true,
  },

  // ---- Payments / refunds ----
  PAYMENT_ALREADY_REFUNDED: {
    title: "Already refunded",
    description: "This payment was already refunded — no further action is needed.",
  },
  PAYMENT_NOT_CAPTURED: {
    title: "Payment not captured",
    description: "This payment hasn't been captured yet, so it can't be refunded.",
  },
  INSUFFICIENT_BALANCE: {
    title: "Insufficient balance",
    description:
      "There isn't enough balance to process this refund. Top up your payment account and retry.",
  },

  // ---- Media / uploads (TikTok, Instagram, etc.) ----
  VIDEO_TOO_LARGE: {
    title: "Video too large",
    description: "This video exceeds the allowed size. Compress it or pick a smaller file.",
  },
  PHOTO_TOO_LARGE: {
    title: "Image too large",
    description: "This image exceeds the allowed size. Use a smaller file.",
  },
  FILE_TOO_LARGE: {
    title: "File too large",
    description:
      "This file is too large to upload. Use a smaller file (check the size limit shown in the uploader) and try again.",
    category: "media",
    severity: "warning",
  },
  TITLE_TOO_LONG: {
    title: "Title too long",
    description: "Shorten the title and try again.",
  },
  INVALID_VIDEO_FORMAT: {
    title: "Unsupported video",
    description: "This video format isn't supported. Use MP4 and try again.",
  },
  INVALID_PHOTO_FORMAT: {
    title: "Unsupported image",
    description: "This image format isn't supported. Use JPG or PNG and try again.",
  },

  // ---- Generic transport (also reachable via patterns below) ----
  OFFLINE: {
    title: "You're offline",
    description: "Your device isn't connected to the internet. Reconnect and try again.",
    severity: "warning",
    retryable: true,
    category: "network",
  },
  NETWORK_ERROR: {
    title: "Connection problem",
    description: "We couldn't reach the server. Check your internet connection and try again.",
    severity: "warning",
    retryable: true,
    category: "network",
  },
  TIMEOUT: {
    title: "Request timed out",
    description: "That took too long to respond. Please try again.",
  },
  MICROSERVICE_ERROR: {
    title: "Service error",
    description: "An internal service had a problem. Please try again in a moment.",
  },
  VALIDATION_ERROR: {
    title: "Check your input",
    description: "Some details look invalid. Review the form and try again.",
  },
  VALIDATION_FAILED: {
    title: "Check your input",
    description: "Some details look invalid. Review the form and try again.",
  },
  INTERNAL_ERROR: {
    title: "Something went wrong",
    description: "Something went wrong on our end. Please try again.",
    category: "server",
    severity: "error",
    retryable: true,
  },

  // ---- WhatsApp Business Platform (Meta) ----
  PHONE_NUMBER_RESTRICTED: {
    title: "Number restricted",
    description:
      "This WhatsApp number is temporarily restricted by Meta. Check its status in WhatsApp Manager before sending again.",
  },
  PHONE_NUMBER_BANNED: {
    title: "Number banned",
    description:
      "Meta has banned this WhatsApp number. You can't send from it until it's reinstated — contact support.",
  },
  PHONE_QUALITY_LOW: {
    title: "Number quality is low",
    description:
      "This number's quality rating has dropped. Reduce blocks and spam reports to recover it before sending more campaigns.",
  },
  MESSAGE_LIMIT_REACHED: {
    title: "Daily message limit reached",
    description:
      "You've hit your WhatsApp messaging limit for now. It resets within 24 hours, or improve number quality to raise the limit.",
  },
  MARKETING_MESSAGE_BLOCKED: {
    title: "Marketing message blocked",
    description:
      "Meta limited this marketing message to protect the customer's experience. Try later or send it as a service message.",
  },
  USER_OPTED_OUT: {
    title: "Customer opted out",
    description:
      "This customer chose to stop receiving messages. You can't message them until they opt back in.",
  },
  RECIPIENT_NOT_ON_WHATSAPP: {
    title: "Not on WhatsApp",
    description:
      "This number isn't registered on WhatsApp, so the message can't be delivered. Double-check the number.",
  },
  INVALID_PHONE_NUMBER: {
    title: "Invalid phone number",
    description:
      "This phone number isn't in a valid format. Include the country code and remove spaces or symbols.",
  },
  TEMPLATE_PAUSED: {
    title: "Template paused",
    description:
      "This template was paused because its quality rating dropped. Use another approved template, or edit and resubmit this one.",
  },
  TEMPLATE_DISABLED: {
    title: "Template disabled",
    description:
      "This template was disabled for a policy reason and can't be used. Create a new one that follows WhatsApp's messaging rules.",
  },
  MEDIA_UPLOAD_EXPIRED: {
    title: "Media expired",
    description: "The uploaded media is no longer available. Re-upload the file and try again.",
  },
  UNSUPPORTED_MESSAGE: {
    title: "Unsupported message",
    description: "This type of message isn't supported here. Try a text, media, or template message.",
  },
  TEMPLATE_PARAMS_MISMATCH: {
    title: "Template details don't match",
    description:
      "The template's placeholders don't match the values provided. Refresh the template and re-enter the values.",
  },
  TEMPLATE_TOO_LONG: {
    title: "Template message too long",
    description:
      "The filled-in template exceeds WhatsApp's length limit. Shorten the variable values and try again.",
  },
  ACCOUNT_BILLING_ISSUE: {
    title: "Billing issue on this account",
    description:
      "This WhatsApp Business account has a billing or eligibility issue. Resolve it in WhatsApp Manager before sending.",
  },
  CANT_MESSAGE_RECIPIENT: {
    title: "Can't message this number",
    description:
      "WhatsApp won't deliver to this number right now. Confirm it's a valid recipient and not your own number.",
  },
  REQUEST_REJECTED: {
    title: "Request couldn't be processed",
    description: "We couldn't process your request. Please refresh the page and try again.",
  },

  // ---- Facebook / Instagram ----
  INSTAGRAM_NOT_CONNECTED: {
    title: "Instagram not connected",
    description: "No Instagram account is connected. Connect one in Integrations to use this feature.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix: "No Instagram account is connected. Ask an admin on your team to connect one in Integrations.",
  },
  PAGE_DISCONNECTED: {
    title: "Facebook Page disconnected",
    description: "This Facebook Page is no longer connected. Reconnect it in Integrations to continue.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix: "This Facebook Page is no longer connected. Ask an admin on your team to reconnect it.",
  },
  INSTAGRAM_PERMISSION_DENIED: {
    title: "Missing permissions",
    description:
      "Some required Instagram/Facebook permissions are missing. Reconnect the account and accept all requested permissions.",
  },
  PAGE_PUBLISH_AUTH_FAILED: {
    title: "Publishing not authorized",
    description: "We aren't authorized to publish to this Page. Reconnect it and grant publishing access.",
  },
  ACCOUNT_RESTRICTED: {
    title: "Account restricted",
    description:
      "This account is temporarily restricted by Meta. Check its status in Meta Business Suite.",
  },
  CONTENT_POLICY_VIOLATION: {
    title: "Content not allowed",
    description: "Meta flagged this content as against its policies. Edit it and try again.",
  },
  POST_SCHEDULING_FAILED: {
    title: "Couldn't schedule post",
    description:
      "The post couldn't be scheduled. Check the date/time and the connected account, then try again.",
  },

  // ---- Ads / Campaigns (Meta Business) ----
  AD_ACCOUNT_DISABLED: {
    title: "Ad account disabled",
    description: "This ad account is disabled by Meta. Resolve the issue in Meta Ads Manager before running ads.",
  },
  AD_ACCOUNT_RESTRICTED: {
    title: "Ad account restricted",
    description: "This ad account is restricted. Check Meta Ads Manager for the required action.",
  },
  INSUFFICIENT_AD_BALANCE: {
    title: "Not enough ad balance",
    description: "There isn't enough balance to run this ad. Add funds to your ad account and try again.",
  },
  CAMPAIGN_BUDGET_EXCEEDED: {
    title: "Budget exceeded",
    description: "This campaign has used its full budget. Increase the budget to keep it running.",
  },
  CAMPAIGN_LIMIT_REACHED: {
    title: "Campaign limit reached",
    description: "You've reached the maximum number of campaigns. Pause or remove one before creating another.",
  },
  PIXEL_NOT_FOUND: {
    title: "Pixel not found",
    description: "No Meta Pixel is set up for this account. Add a Pixel to track conversions.",
  },
  AUDIENCE_TOO_SMALL: {
    title: "Audience too small",
    description: "This audience is too small to run an ad. Broaden your targeting and try again.",
  },

  // ---- Automation flows ----
  FLOW_LOOP_DETECTED: {
    title: "Flow has a loop",
    description: "This flow loops back on itself and can't run. Remove the circular connection between steps.",
  },
  FLOW_PUBLISH_FAILED: {
    title: "Couldn't publish flow",
    description: "The flow couldn't be published. Make sure every step is complete and connected, then try again.",
  },
  FLOW_TRIGGER_MISSING: {
    title: "Flow needs a trigger",
    description: "Add a starting trigger to this flow before publishing it.",
  },
  FLOW_NODE_INVALID: {
    title: "A flow step is incomplete",
    description: "One of the steps is missing required details. Open each step and fill in the highlighted fields.",
  },
  FLOW_EXECUTION_FAILED: {
    title: "Flow run failed",
    description: "The flow couldn't finish running. Check its steps and try again.",
  },
  WEBHOOK_FAILED: {
    title: "Webhook failed",
    description: "We couldn't reach the webhook URL. Verify the endpoint is online and try again.",
  },

  // ---- Webhooks / integrations ----
  WEBHOOK_TIMEOUT: {
    title: "Webhook didn't respond in time",
    description: "Your webhook URL took too long to reply. Make sure it's online and responds quickly, then try again.",
  },
  WEBHOOK_SIGNATURE_INVALID: {
    title: "Couldn't verify the connection",
    description:
      "We couldn't securely verify data from the connected service. Reconnect the integration, or contact your administrator.",
  },
  THIRD_PARTY_SERVICE_DOWN: {
    title: "Service unavailable",
    description: "A connected service is temporarily down. Please try again in a little while.",
  },
  INTEGRATION_DISCONNECTED: {
    title: "Integration disconnected",
    description: "This integration is no longer connected. Reconnect it in Integrations to continue.",
    fixableBy: "admin",
    action: { label: "Go to Integrations", to: "/home/integrations" },
    whenCannotFix:
      "This integration is no longer connected. Ask an admin on your team to reconnect it in Integrations.",
  },

  // ---- File uploads / media ----
  FILE_CORRUPTED: {
    title: "File is corrupted",
    description: "This file appears to be damaged and can't be used. Try a different copy.",
  },
  FILE_UPLOAD_FAILED: {
    title: "Upload failed",
    description: "The file couldn't be uploaded. Check your connection and try again.",
  },
  FILE_SCAN_FAILED: {
    title: "Couldn't scan file",
    description: "We couldn't safety-scan this file. Try again, or use a different file.",
  },
  MEDIA_PROCESSING_FAILED: {
    title: "Media processing failed",
    description: "We couldn't process this media. Try a different file or format.",
  },

  // ---- Misc backend codes that need specific copy (long tail handled by inference) ----
  CONFIG_MISSING: {
    title: "Not set up yet",
    description: "This feature isn't fully set up. Finish configuring it in Settings to continue.",
    fixableBy: "admin",
    action: { label: "Open Settings", to: "/home/settings" },
    whenCannotFix: "This feature isn't fully set up. Ask your administrator to finish configuring it.",
  },
  AUTH_ERROR: {
    title: "Please sign in again",
    description: "Your session looks invalid. Sign in again to continue.",
  },
  GOOGLE_REJECTED: {
    title: "Request rejected",
    description: "Google didn't accept that request. Adjust your input and try again.",
  },
  INVALID_QUERY: {
    title: "Check your search",
    description: "That search wasn't valid. Adjust the terms and try again.",
  },
  TIKTOK_IDENTITY_DEPRECATED: {
    title: "Reconnect TikTok",
    description: "Your TikTok connection needs refreshing. Reconnect the account in Integrations to continue.",
  },
  VIDEO_CREATE_NOT_SUPPORTED: {
    title: "Video not supported here",
    description: "This account can't create video posts through this feature yet.",
  },
  INSTANTIATE_FAILED_ROLLED_BACK: {
    title: "Couldn't create it",
    description: "Something went wrong while creating this, so no changes were saved. Please try again.",
  },

  // ---- TikTok — Content Posting API ----
  // TikTok forwards its OWN lowercase error codes straight through (see
  // ERROR_STATUS_MAP in TikTokContentPostingService). They don't match the
  // SCREAMING_SNAKE shape-inference, so they need explicit entries here.
  // Generically-named codes get provider-neutral copy (another API could emit
  // the same string); clearly-TikTok-named codes name TikTok.
  access_token_invalid: {
    title: "Reconnect required",
    description: "This account's connection has expired. Reconnect it in Integrations and try again.",
  },
  scope_not_authorized: {
    title: "Permission missing",
    description: "A required permission wasn't granted. Reconnect the account and accept all requested permissions.",
  },
  token_not_authorized_for_specified_publish_id: {
    title: "Reconnect TikTok",
    description: "This TikTok post can't be accessed with the current connection. Reconnect the account and try again.",
  },
  spam_risk_too_many_posts: {
    title: "TikTok daily post limit reached",
    description: "TikTok has temporarily capped how many posts this account can publish. Wait a while before posting again.",
    category: "rate_limit",
    severity: "info",
  },
  spam_risk_user_banned_from_posting: {
    title: "Posting blocked by TikTok",
    description: "TikTok has blocked this account from posting. Check the account's status in the TikTok app.",
  },
  reached_active_user_cap: {
    title: "Too many uploads at once",
    description: "TikTok is still processing your recent uploads. Wait for them to finish, then try again.",
  },
  unaudited_client_can_only_post_to_private_accounts: {
    title: "Public posting not enabled yet",
    description:
      "Until our TikTok app finishes review, posts can only go to private TikTok accounts. Set the account to private, or contact support.",
  },
  url_ownership_unverified: {
    title: "Verify your domain with TikTok",
    description:
      "TikTok needs the link's domain verified before it can be posted. Verify domain ownership in your TikTok developer settings.",
  },
  privacy_level_option_mismatch: {
    title: "Pick a privacy setting",
    description: "Choose a valid privacy level (public, friends, or private) for this TikTok post and try again.",
  },
  rate_limit_exceeded: {
    title: "Slow down a moment",
    description: "You've hit the rate limit. Wait a little while and try again.",
  },

  // ---- TikTok — content service + ads (our own codes) ----
  UNSUPPORTED_VIDEO_FORMAT: {
    title: "Unsupported video",
    description: "This video format isn't supported. Use MP4 (H.264) and try again.",
  },
  INVALID_VIDEO_SIZE: {
    title: "Video size not allowed",
    description: "This video's size is outside the allowed range. Use a smaller file and try again.",
  },
  VIDEO_DOWNLOAD_FAILED: {
    title: "Couldn't fetch the video",
    description: "We couldn't download the video to upload it. Check the file link and try again.",
  },
  EMPTY_VIDEO: {
    title: "Video file is empty",
    description: "The video file has no content. Re-export or pick a different file.",
  },
  TIKTOK_CONTENT_ERROR: {
    title: "TikTok couldn't process this",
    description: "TikTok rejected this content. Check the video/photo and caption, then try again.",
  },
  TIKTOK_ERROR: {
    title: "TikTok error",
    description: "TikTok returned an error. Please try again in a moment.",
  },

  // ---- YouTube ----
  YOUTUBE_NO_REFRESH_TOKEN: {
    title: "Reconnect YouTube",
    description: "Your YouTube connection needs refreshing. Disconnect and reconnect the channel in Integrations.",
  },
  YOUTUBE_REFRESH_NOT_AVAILABLE: {
    title: "Reconnect YouTube",
    description: "Your YouTube connection expired and can't refresh. Reconnect the channel in Integrations.",
  },
  YOUTUBE_TOKEN_EXCHANGE_FAILED: {
    title: "Couldn't connect YouTube",
    description: "We couldn't complete the YouTube connection. Try connecting the channel again.",
  },
  YOUTUBE_NO_ACCESS_TOKEN: {
    title: "Reconnect YouTube",
    description: "Your YouTube sign-in didn't return access. Reconnect the channel in Integrations.",
  },
  YOUTUBE_NO_CHANNEL: {
    title: "No YouTube channel found",
    description: "This Google account has no YouTube channel. Create one on YouTube, then connect again.",
  },
  YOUTUBE_CHANNEL_FETCH_FAILED: {
    title: "Couldn't load your channel",
    description: "We couldn't load your YouTube channel details. Try again in a moment.",
  },
  YOUTUBE_CHANNEL_IN_OTHER_ORG: {
    title: "Channel already connected",
    description: "This YouTube channel is already connected to another workspace. Disconnect it there first.",
  },
  YOUTUBE_INVALID_CHANNEL_RESPONSE: {
    title: "Couldn't read your channel",
    description: "YouTube returned unexpected channel data. Try reconnecting the channel.",
  },

  // ---- LinkedIn ----
  IMAGE_UPLOAD_FAILED: {
    title: "Image upload failed",
    description: "The image couldn't be uploaded. Check the file and try again.",
  },
  IMAGE_FETCH_FAILED: {
    title: "Couldn't fetch the image",
    description: "We couldn't load the image to attach it. Check the image link and try again.",
  },

  // ---- Razorpay ----
  RAZORPAY_API_ERROR: {
    title: "Payment provider error",
    description: "Razorpay returned an error processing that. Please try again in a moment.",
  },
  RAZORPAY_INVALID_CREDENTIALS: {
    title: "Couldn't verify your Razorpay keys",
    description:
      "Razorpay rejected these keys. Double-check the Key ID and Key Secret from your Razorpay dashboard (Settings → API Keys) and try again.",
  },

  // ---- WhatsApp message templates (creation — Meta 2388xxx) ----
  TEMPLATE_LIMIT_REACHED: {
    title: "Template limit reached",
    description: "You've reached the maximum number of message templates. Delete an unused one, then try again.",
  },
  TEMPLATE_FIELD_TOO_LONG: {
    title: "Template text is too long",
    description: "A part of this template is over the character limit. Shorten the header, body, or footer and try again.",
  },
  TEMPLATE_FORMAT_INVALID: {
    title: "Check the template formatting",
    description: "The header, body, or footer formatting isn't valid. Review the template and try again.",
  },
  TEMPLATE_TOO_MANY_VARIABLES: {
    title: "Too many variables",
    description: "This template has too many {{variables}} for its length. Add more text, or remove some variables.",
  },
  TEMPLATE_VARIABLE_POSITION: {
    title: "Move the variable",
    description: "A {{variable}} can't be the very first or last thing in the template. Add some text around it.",
  },

  // ---- WhatsApp Flows / Forms (Meta 132068 / 132069) ----
  FLOW_BLOCKED: {
    title: "This flow is blocked",
    description: "Meta has blocked this WhatsApp flow, so it can't be used right now. Check its status in WhatsApp Manager.",
  },
  FLOW_THROTTLED: {
    title: "Flow is sending too fast",
    description: "This flow hit its hourly limit. Wait a little while, then try again.",
  },

  // ---- WhatsApp messaging (additional Meta codes) ----
  COUNTRY_RESTRICTED: {
    title: "Messaging restricted in this region",
    description: "Meta has restricted messaging to this recipient's country. Contact support if you need help.",
  },
  PAYMENTS_TOS_NOT_ACCEPTED: {
    title: "Accept the WhatsApp Payments terms",
    description: "WhatsApp Payments isn't fully set up. Accept the payments terms in WhatsApp Manager to continue.",
  },

  // ---- Instagram / Facebook content publishing (Meta 2207xxx / 36xxx) ----
  IG_MEDIA_FETCH_TIMEOUT: {
    title: "Couldn't fetch your media",
    description: "Instagram couldn't download your photo or video in time. Use a smaller file or a faster link, then try again.",
  },
  IG_IMAGE_TOO_LARGE: {
    title: "Image too large",
    description: "Instagram images must be under 8 MB. Use a smaller image and try again.",
  },
  IG_UNSUPPORTED_IMAGE: {
    title: "Unsupported image",
    description: "Instagram accepts JPG or PNG images. Convert the file and try again.",
  },
  IG_UNSUPPORTED_VIDEO: {
    title: "Unsupported video",
    description: "Instagram needs an MP4 (H.264) video. Convert the file and try again.",
  },
  IG_ASPECT_RATIO: {
    title: "Image shape not supported",
    description: "This image's shape isn't allowed by Instagram. Crop it between 4:5 (tall) and 1.91:1 (wide) and try again.",
  },
  IG_CAPTION_TOO_LONG: {
    title: "Caption too long",
    description: "Your caption is over Instagram's 2,200-character limit. Shorten it and try again.",
  },
  IG_DAILY_LIMIT_REACHED: {
    title: "Daily post limit reached",
    description: "You've hit Instagram's limit of 100 posts in 24 hours. Please try again later.",
  },
  IG_CONTENT_BLOCKED: {
    title: "Post blocked by Instagram",
    description: "Instagram flagged this content. Edit the image or caption and try again.",
  },
};

/**
 * Raw-string matchers for errors that arrive WITHOUT a machine code — mostly
 * Meta Graph API sentences and browser/network failures. First match wins.
 */
const PATTERN_MATCHERS: Array<{ test: RegExp; friendly: FriendlyError }> = [
  {
    test: /2534022|outside (of )?allowed window|24[\s-]?hour/i,
    friendly: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  },
  {
    test: /\(#?190\)|access token.*(expire|invalid)|session has expired|OAuthException|token.*(expired|invalid)/i,
    friendly: CATALOG.NEEDS_RECONNECT,
  },
  {
    test: /\(#?10\)|\(#?200\)|do(es)? not have (the )?permission|insufficient permission/i,
    friendly: CATALOG.ROLE_FORBIDDEN,
  },
  {
    test: /rate limit|too many requests|#613|#80007|#131056|calls to this api have exceeded|spam rate limit|#131048/i,
    friendly: CATALOG.RATE_LIMITED,
  },

  // ---- Meta WhatsApp Cloud API numeric subcodes (raw strings, no machine code) ----
  {
    test: /\(#?131026\)|message undeliverable|recipient.*not.*whatsapp|not a (valid )?whatsapp user/i,
    friendly: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  },
  {
    test: /\(#?13104[79]\)|re-?engagement message|healthy ecosystem|maintain.*engagement/i,
    friendly: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  },
  {
    test: /\(#?131051\)|unsupported message type/i,
    friendly: {
      title: "Unsupported message",
      description: "This type of message isn't supported here. Try a text, media, or template message.",
    },
  },
  {
    test: /\(#?13105[23]\)|\(#?131057\)|media (download|upload) error|failed to (download|upload) media/i,
    friendly: CATALOG.MEDIA_UPLOAD_EXPIRED,
  },
  {
    test: /\(#?132015\)|template is paused/i,
    friendly: CATALOG.TEMPLATE_PAUSED,
  },
  {
    test: /\(#?132016\)|template is disabled/i,
    friendly: CATALOG.TEMPLATE_DISABLED,
  },
  {
    test: /\(#?132001\)|template (name )?does not exist|template not found|no.*matching template/i,
    friendly: CATALOG.TEMPLATE_NOT_ON_META,
  },
  {
    test: /\(#?132000\)|number of (localizable_)?params|param(eter)?s? (do(es)? not match|mismatch)/i,
    friendly: {
      title: "Template details don't match",
      description:
        "The template's placeholders don't match the values provided. Refresh the template and re-enter the values.",
    },
  },
  {
    test: /\(#?13200[57]\)|\(#?132012\)|template.*(policy|content|format).*(violat|invalid|mismatch)/i,
    friendly: CATALOG.CONTENT_POLICY_VIOLATION,
  },
  {
    test: /\(#?131031\)|\(#?368\)|account has been locked|temporarily blocked.*polic|business account.*restrict/i,
    friendly: CATALOG.ACCOUNT_RESTRICTED,
  },
  {
    // Raw DB/SQL errors must NEVER reach the user — hide internals behind a
    // generic server message (e.g. "Table '...' doesn't exist; Error code: 1146").
    test: /SQLState|ER_NO_SUCH_TABLE|Unknown column|SQL syntax|Table '[^']*' doesn'?t exist|doesn'?t exist; Error code|Deadlock found|ECONNREFUSED.*(mysql|3306|5432)/i,
    friendly: CATALOG.INTERNAL_ERROR,
  },
  {
    // Google Ads / JSON-API payload errors ("Invalid JSON payload received.
    // Unknown name 'startDate' at 'operations[0].update'").
    test: /Invalid JSON payload|Unknown name '[^']+' at|operations\[\d+\]\.(update|create|remove)/i,
    friendly: CATALOG.REQUEST_REJECTED,
  },
  {
    // Org payment gateway not connected — connectors return null and downstream
    // handlers surface prose like "No active Razorpay connection". Gateway-specific
    // patterns first, then the generic "no active connection found" fallback.
    test: /no active razorpay connection|razorpay.*(not connected|isn'?t connected)|connect (your )?razorpay/i,
    friendly: CATALOG.RAZORPAY_NOT_CONNECTED,
  },
  {
    test: /no active payu connection|payu.*(not connected|isn'?t connected)|connect (your )?payu/i,
    friendly: CATALOG.PAYU_NOT_CONNECTED,
  },
  {
    test: /no active cashfree connection|cashfree.*(not connected|isn'?t connected)|connect (your )?cashfree/i,
    friendly: CATALOG.CASHFREE_NOT_CONNECTED,
  },
  {
    test: /no active connection found|no payment (gateway|provider|method).*(connected|configured)|payment (gateway|provider).*not connected/i,
    friendly: CATALOG.PAYMENT_NOT_CONNECTED,
  },
  {
    // Shopify (and similar) app-approval / protected-data errors.
    test: /not approved to access.*(REST|endpoint|protected)|protected customer data/i,
    friendly: {
      title: "Approval needed for this data",
      description:
        "This integration needs additional approval from the provider before it can access that data. Contact support to enable it.",
    },
  },
  {
    // Shopify store not connected — thrown by ShopifyConnectorService.getConnection.
    test: /no active shopify connection|shopify.*not connected|no shopify connection/i,
    friendly: {
      title: "Shopify not connected",
      description: "No Shopify store is connected. Go to Integrations → Shopify to connect your store.",
    },
  },
  {
    // Shopify token decrypt failure — user must reconnect the store.
    test: /failed to decrypt shopify|reconnect your shopify store/i,
    friendly: {
      title: "Reconnect your Shopify store",
      description: "Your Shopify connection needs to be refreshed. Go to Integrations → Shopify and reconnect.",
    },
  },
  {
    // Shopify API rate limit.
    test: /shopify api error 429|shopify.*rate.?limit|exceeded.*shopify.*limit/i,
    friendly: {
      title: "Shopify rate limit",
      description: "Shopify is rate-limiting requests right now. Wait a moment and try again.",
      severity: "info",
      retryable: true,
    },
  },
  {
    // Shopify store frozen / closed / suspended (unpaid bill, store paused).
    test: /shop(ify)?.*(is frozen|frozen|has been (closed|paused|suspended)|store is unavailable|is closed)/i,
    friendly: {
      title: "Shopify store unavailable",
      description:
        "Your Shopify store is frozen or closed (often an unpaid Shopify bill). Resolve it in your Shopify admin, then try again.",
    },
  },
  {
    // Shopify generic API error (e.g. "Shopify API error 422").
    test: /shopify api error \d+/i,
    friendly: {
      title: "Shopify error",
      description: "Shopify returned an error. Check the product details and try again.",
    },
  },
  {
    // YouTube quota — Google returns 403 "quotaExceeded" / daily quota prose.
    test: /quotaexceeded|youtube.*quota|daily.*quota.*(exceed|reached)|exceeded your.*quota/i,
    friendly: CATALOG.QUOTA_EXCEEDED,
  },
  {
    // YouTube connection needs refreshing — code-less prose from YouTube services
    // ("no refresh_token persisted", "disconnect and reconnect", "Cannot refresh YouTube token").
    test: /youtube.*(refresh token|disconnect and reconnect|reconnect)|cannot refresh youtube token|no refresh_token persisted/i,
    friendly: CATALOG.NEEDS_RECONNECT,
  },
  {
    test: /\(#?100\)|invalid parameter|GraphMethodException/i,
    friendly: {
      title: "Request couldn't be processed",
      description: "We couldn't process your request. Please refresh the page and try again.",
    },
  },
  {
    // Raw Meta Graph API errors that leak developer-facing text + doc links
    // (e.g. "Unsupported post request. Object with ID '…' does not exist, cannot
    // be loaded due to missing permissions … Please read the Graph API
    // documentation at https://developers.facebook.com/docs/graph-api").
    test: /unsupported (post|get) request|object with id .* does not exist|graph api documentation|developers\.facebook\.com\/docs/i,
    friendly: {
      title: "Something went wrong with Meta",
      description: "Meta couldn't complete this action. Your connected account may be unavailable or missing a permission. Refresh and try again, or reconnect it in Integrations.",
    },
  },
  {
    // A webhook/endpoint the customer configured didn't respond in time (distinct
    // from our own request timing out).
    test: /endpoint request timed out|webhook.*tim(ed|e)?.?out/i,
    friendly: CATALOG.WEBHOOK_TIMEOUT,
  },
  {
    // AI flow generator (microservice) — "Query must be less than 2000 characters".
    // "Query" is the service's word for the prompt; users read it as SQL, so
    // rephrase to the thing they actually typed.
    test: /query must be less than \d+ characters|(prompt|description|query).*(exceed|too long|less than).*\d+ character/i,
    friendly: {
      title: "Description is too long",
      description: "Your flow description is over the character limit. Shorten it and try again.",
      category: "validation",
      severity: "warning",
    },
  },
  {
    // Instagram/Facebook publishing — codeless prose variants of the 2207xxx/36xxx codes.
    test: /aspect ratio|image.*(shape|ratio).*(not supported|unsupported)/i,
    friendly: CATALOG.IG_ASPECT_RATIO,
  },
  {
    test: /caption.*(exceed|too long|2,?200)|2,?200[\s-]?character/i,
    friendly: CATALOG.IG_CAPTION_TOO_LONG,
  },
  {
    test: /\b100 posts\b|posts? per (24 hours|day)|daily.*post.*limit|reached.*post.*limit/i,
    friendly: CATALOG.IG_DAILY_LIMIT_REACHED,
  },
  {
    test: /timeout downloading media|failed to (download|fetch) media|media fetch failed/i,
    friendly: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  },
  {
    // WhatsApp Flow blocked/throttled as prose.
    test: /flow is (in a )?blocked( state)?|flow has been blocked/i,
    friendly: CATALOG.FLOW_BLOCKED,
  },
  {
    // WhatsApp template-creation prose (Meta 2388xxx).
    test: /template (count |limit ).*(exceed|reach)|maximum.*templates|reached.*template limit/i,
    friendly: CATALOG.TEMPLATE_LIMIT_REACHED,
  },
  {
    // Catalog product-not-found (Meta 131009 surfaced as prose with raw IDs):
    // "Product not found in catalog ... product_retailer_id ... does not exist".
    test: /product not found in catalog|product_retailer_id|product.*(not (found|exist)|does not exist).*catalog|catalog.*does not (contain|exist)/i,
    friendly: CATALOG.PRODUCT_NOT_IN_CATALOG,
  },
  {
    // Catalog connect/link failures from MetaCommerceService, e.g. "Catalog
    // 132636... is not accessible with the current token. Ensure this token/user
    // has access..." or "Catalog access denied ... Reconnect Meta with catalog
    // permissions". Belt-and-suspenders for paths that emit prose without the
    // CATALOG_NOT_ACCESSIBLE machine code (relink/sync, older backend deploys).
    test: /catalog .*not accessible with the current token|catalog access denied/i,
    friendly: CATALOG.CATALOG_NOT_ACCESSIBLE,
  },
  {
    // Backend template-component validation, e.g. "HEADER component with TEXT
    // format requires 'text' field" or "BODY component ... required".
    test: /\b(HEADER|BODY|FOOTER|BUTTONS?)\b.*(component|format).*(require|missing|empty)|requires '?\w+'? field|component.*is required/i,
    friendly: CATALOG.TEMPLATE_INCOMPLETE,
  },

  {
    // File/upload too large. Upload call sites embed the HTTP status in the
    // message string ("Upload failed (413)"), so the status-code tier never
    // sees it — match the text form here. Also covers raw 413 prose, the
    // serverless gateway's "HTTP content length exceeded N bytes." body (the
    // rare case where it arrives readable instead of as a connection reset),
    // and AWS API Gateway's 413 body `{"message":"Request Too Long"}` (seen
    // 2026-07-04 when a base64-encoded product image blew the ~10 MB cap).
    test: /upload failed \(413\)|\b413\b[^]*(upload|too large|payload|entity)|payload too large|request entity too large|request too long|file (is )?too large|exceeds.*(file )?size limit|content length exceeded/i,
    friendly: CATALOG.FILE_TOO_LARGE,
  },
  {
    // Payment-link / amount-cap errors that arrive as raw prose without a code
    // ("amount exceeds maximum amount allowed").
    test: /amount exceeds (the )?maximum|exceeds maximum amount|amount is too large|maximum amount allowed/i,
    friendly: CATALOG.AMOUNT_TOO_LARGE,
  },
  {
    // Razorpay key validation failure. Matches even when the backend appends a
    // broken "...: undefined" (SDK error has no .message) — the user should
    // never see "undefined" regardless of what the backend sent.
    test: /invalid razorpay credentials|razorpay.*(credential|key).*(invalid|rejected)|authentication failed.*razorpay/i,
    friendly: CATALOG.RAZORPAY_INVALID_CREDENTIALS,
  },
  {
    test: /failed to fetch|networkerror|err_connection|load failed|net::|connection refused|socket hang ?up|ECONNREFUSED|ECONNRESET|ENOTFOUND|EHOSTUNREACH|ENETUNREACH|EPIPE|read ECONN|write ECONN/i,
    friendly: CATALOG.NETWORK_ERROR,
  },
  {
    test: /timed out|timeout|ETIMEDOUT|ECONNABORTED/i,
    friendly: CATALOG.TIMEOUT,
  },
  {
    test: /internal server error|bad gateway|service unavailable|gateway timeout|\b50[0-4]\b|HTTP\s*5\d\d/i,
    friendly: {
      title: "Server problem",
      description: "We're having trouble on our end. Please try again in a few moments.",
    },
  },
  {
    // Bare "HTTP 4xx" strings (e.g. "HTTP 400", "HTTP 404") with no useful body —
    // surfaced raw by some call sites. 401/403 already mapped via the code tiers;
    // this is the last-resort net so the user never sees "HTTP 400".
    test: /\bHTTP\s*4\d\d\b/i,
    friendly: {
      title: "Request couldn't be processed",
      description: "We couldn't process your request. Please refresh the page and try again.",
      category: "validation",
      severity: "warning",
    },
  },
];

/**
 * Numeric Meta error codes → friendly copy. ONLY codes this app actually
 * encounters (sourced from backend handlers: metaTokenExpiryDetector,
 * BoardService, CampaignExecutionService, ProviderDeliveryService,
 * Facebook/Instagram API services, whatsappRoutes). Keep this list to codes we
 * really use — do not pad it with codes the app never receives.
 */
const META_CODE_MAP: Record<number, FriendlyError> = {
  // Auth / token / permission (Graph + Cloud API)
  0: CATALOG.NEEDS_RECONNECT,
  3: CATALOG.NEEDS_RECONNECT,
  10: CATALOG.INSTAGRAM_PERMISSION_DENIED,
  102: CATALOG.NEEDS_RECONNECT,
  110: CATALOG.NEEDS_RECONNECT,
  190: CATALOG.NEEDS_RECONNECT,
  200: CATALOG.INSTAGRAM_PERMISSION_DENIED,
  // Transient Meta service error ("retry later") — surfaces only after retries exhaust
  2: CATALOG.THIRD_PARTY_SERVICE_DOWN,
  // Throttling / rate limits
  4: CATALOG.RATE_LIMITED,
  32: CATALOG.RATE_LIMITED,
  613: CATALOG.RATE_LIMITED,
  80007: CATALOG.RATE_LIMITED,
  130429: CATALOG.RATE_LIMITED,
  131048: CATALOG.RATE_LIMITED,
  131056: CATALOG.RATE_LIMITED,
  131057: CATALOG.RATE_LIMITED,
  // Integrity / blocking / eligibility
  368: CATALOG.ACCOUNT_RESTRICTED,
  131031: CATALOG.ACCOUNT_RESTRICTED,
  131042: CATALOG.ACCOUNT_BILLING_ISSUE,
  // Message-sending failures
  131000: CATALOG.INTERNAL_ERROR,
  131005: CATALOG.ROLE_FORBIDDEN,
  131008: CATALOG.VALIDATION_ERROR,
  131009: CATALOG.VALIDATION_ERROR,
  131016: CATALOG.THIRD_PARTY_SERVICE_DOWN,
  131021: CATALOG.CANT_MESSAGE_RECIPIENT,
  131026: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  1013: CATALOG.RECIPIENT_NOT_ON_WHATSAPP,
  131045: CATALOG.ACCOUNT_BILLING_ISSUE, // phone registration / certificate issue → admin action
  131047: CATALOG.WHATSAPP_WINDOW_EXPIRED,
  131049: CATALOG.MARKETING_MESSAGE_BLOCKED,
  131050: CATALOG.USER_OPTED_OUT, // recipient stopped marketing messages
  131051: CATALOG.UNSUPPORTED_MESSAGE,
  131052: CATALOG.MEDIA_UPLOAD_EXPIRED,
  131053: CATALOG.MEDIA_UPLOAD_EXPIRED,
  130472: CATALOG.MARKETING_MESSAGE_BLOCKED, // user in Meta experiment — message not delivered
  130497: CATALOG.COUNTRY_RESTRICTED,
  134011: CATALOG.PAYMENTS_TOS_NOT_ACCEPTED,
  135000: CATALOG.INTERNAL_ERROR,
  // Templates (sending)
  132000: CATALOG.TEMPLATE_PARAMS_MISMATCH,
  132001: CATALOG.TEMPLATE_NOT_ON_META,
  132005: CATALOG.TEMPLATE_TOO_LONG,
  132007: CATALOG.CONTENT_POLICY_VIOLATION,
  132012: CATALOG.TEMPLATE_PARAMS_MISMATCH,
  132015: CATALOG.TEMPLATE_PAUSED,
  132016: CATALOG.TEMPLATE_DISABLED,
  // Templates (creation — Meta 2388xxx)
  2388019: CATALOG.TEMPLATE_LIMIT_REACHED,
  2388040: CATALOG.TEMPLATE_FIELD_TOO_LONG,
  2388047: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388072: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388073: CATALOG.TEMPLATE_FORMAT_INVALID,
  2388293: CATALOG.TEMPLATE_TOO_MANY_VARIABLES,
  2388299: CATALOG.TEMPLATE_VARIABLE_POSITION,
  // WhatsApp Flows / Forms
  132068: CATALOG.FLOW_BLOCKED,
  132069: CATALOG.FLOW_THROTTLED,
  // Instagram / Facebook content publishing
  9004: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207003: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  2207004: CATALOG.IG_IMAGE_TOO_LARGE,
  2207005: CATALOG.IG_UNSUPPORTED_IMAGE,
  2207006: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207008: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207009: CATALOG.IG_ASPECT_RATIO,
  2207010: CATALOG.IG_CAPTION_TOO_LONG,
  2207020: CATALOG.MEDIA_UPLOAD_EXPIRED,
  2207026: CATALOG.IG_UNSUPPORTED_VIDEO,
  2207032: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  2207042: CATALOG.IG_DAILY_LIMIT_REACHED,
  2207050: CATALOG.ACCOUNT_RESTRICTED,
  2207001: CATALOG.IG_CONTENT_BLOCKED,
  2207051: CATALOG.RATE_LIMITED,
  2207053: CATALOG.IG_MEDIA_FETCH_TIMEOUT,
  36001: CATALOG.IG_ASPECT_RATIO,
  36003: CATALOG.IG_ASPECT_RATIO,
  2534022: CATALOG.WHATSAPP_WINDOW_EXPIRED,
};

/** Numeric Meta error SUBCODES → friendly copy. Subcode wins over code (more specific). */
const META_SUBCODE_MAP: Record<number, FriendlyError> = {
  33: CATALOG.INSTAGRAM_PERMISSION_DENIED, // code 100 + subcode 33 = missing scope
  458: CATALOG.NEEDS_RECONNECT, // user has not authorized application
  463: CATALOG.NEEDS_RECONNECT, // token expired
  467: CATALOG.NEEDS_RECONNECT, // invalid access token
  1404169: CATALOG.RATE_LIMITED, // IG DM 368 cooldown
  2207013: CATALOG.NEEDS_RECONNECT, // IG token/object expired
  2207052: CATALOG.MEDIA_UPLOAD_EXPIRED, // IG media download failed
  2534022: CATALOG.WHATSAPP_WINDOW_EXPIRED, // outside 24h window
};

/** Pull a numeric value from a string|number|undefined; returns null if not finite. */
function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Extract the first Meta-style "(#12345)" numeric code from a raw message, if any. */
function extractMetaCodeFromText(raw?: string): number | null {
  if (!raw) return null;
  const m = raw.match(/\(#?(\d{2,7})\)/);
  return m ? Number(m[1]) : null;
}

/**
 * HTTP status → friendly, for when the backend gives only a status code with no
 * useful body. 5xx / 401 / 403 / 429 always win over the raw text (their bodies
 * are usually technical); 4xx defer to a human message when one exists.
 */
const HTTP_STATUS_MAP: Record<number, FriendlyError> = {
  400: {
    title: "Check your input",
    description: "Some details weren't accepted. Review what you entered and try again.",
    category: "validation",
    severity: "warning",
  },
  401: CATALOG.UNAUTHORIZED,
  403: CATALOG.ROLE_FORBIDDEN,
  404: {
    title: "Not found",
    description: "We couldn't find what you're looking for. It may have been moved or deleted.",
    category: "not_found",
    severity: "warning",
  },
  409: {
    title: "That conflicts with something",
    description: "This conflicts with something that already exists or is already running. Refresh and try again.",
    category: "validation",
    severity: "warning",
  },
  413: CATALOG.FILE_TOO_LARGE,
  422: CATALOG.VALIDATION_ERROR,
  429: CATALOG.RATE_LIMITED,
  500: CATALOG.INTERNAL_ERROR,
  502: {
    title: "Server problem",
    description: "We're having trouble on our end. Please try again in a few moments.",
    category: "server",
    severity: "error",
    retryable: true,
  },
  503: CATALOG.THIRD_PARTY_SERVICE_DOWN,
  504: CATALOG.TIMEOUT,
};

/**
 * Code-shape inference for unmapped SCREAMING_SNAKE codes. Turns the long tail
 * of backend codes into friendly copy by keyword/suffix, instead of leaking
 * "Missing publish id." style jargon. Returns null when nothing matches.
 */
function inferFromCodeShape(code: string): FriendlyError | null {
  const c = code.toUpperCase();
  if (c === "NOT_FOUND" || c.endsWith("_NOT_FOUND"))
    return {
      title: "Not found",
      description: "We couldn't find what you're looking for. It may have been moved or deleted.",
      category: "not_found",
      severity: "warning",
    };
  if (/UNAUTHORIZED|TOKEN_EXPIRED|SESSION_|(^|_)AUTH(_|$)/.test(c))
    return { ...CATALOG.UNAUTHORIZED, category: "authentication", severity: "warning" };
  if (/FORBIDDEN|PERMISSION|NOT_ALLOWED|ACCESS_DENIED|(^|_)ROLE_/.test(c))
    return { ...CATALOG.ROLE_FORBIDDEN, category: "permission", severity: "warning" };
  if (/NETWORK|ECONN|FETCH/.test(c))
    return { ...CATALOG.NETWORK_ERROR };
  if (/RATE_LIMIT|TOO_MANY_REQUESTS|QUOTA|LIMIT_REACHED|LIMIT_EXCEEDED/.test(c))
    return { ...CATALOG.RATE_LIMITED, category: "rate_limit", severity: "info" };
  if (/NOT_CONFIGURED|CONFIG_MISSING|NOT_SET_UP|MISSING_CONFIG/.test(c))
    return { ...CATALOG.CONFIG_MISSING, category: "integration", severity: "warning" };
  if (/UNAVAILABLE|SERVICE_DOWN|(^|_)QUEUE_/.test(c))
    return { ...CATALOG.QUEUE_UNAVAILABLE, category: "server", severity: "warning", retryable: true };
  if (/TOO_LARGE|TOO_LONG|TOO_MANY|EXCEED/.test(c))
    return {
      title: "That's too large",
      description: "That's larger than allowed. Use a smaller value or file and try again.",
      category: "validation",
      severity: "warning",
    };
  if (/INVALID_|_INVALID$|MALFORMED|(^|_)BAD_/.test(c))
    return { ...CATALOG.VALIDATION_ERROR, category: "validation", severity: "warning" };
  if (/MISSING_|_REQUIRED$|REQUIRED_|EMPTY_|_EMPTY$/.test(c))
    return {
      title: "Missing required information",
      description: "Something required is missing. Fill in the highlighted fields and try again.",
      category: "validation",
      severity: "warning",
    };
  if (/CONFLICT|ALREADY_|_IN_USE$|(^|_)IN_USE/.test(c))
    return {
      title: "That already exists",
      description: "This conflicts with something that already exists. Refresh and try again.",
      category: "validation",
      severity: "warning",
    };
  if (/_FAILED$|FAILED_|_ERROR$|(^|_)ERROR_/.test(c))
    return {
      title: "Couldn't complete the action",
      description: "Something went wrong while completing your request. Please try again.",
      category: "unknown",
      severity: "error",
      retryable: true,
    };
  return null;
}

/** True when the device reports being offline (browser only; SSR-safe). */
function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

const RETRYABLE_TITLE_RE =
  /connection problem|you're offline|timed out|server problem|service (unavailable|busy)|slow down|temporarily/i;

/**
 * Fill in severity / retryable / category when the matched entry didn't set them
 * explicitly — inferred from the copy so every resolved error carries metadata.
 */
function classify(friendly: FriendlyError): FriendlyError {
  const text = `${friendly.title} ${friendly.description}`.toLowerCase();

  let category = friendly.category;
  if (category == null) {
    if (/sign in|session|reconnect required|token/.test(text)) category = "authentication";
    else if (/permission|not allowed|ask an admin|ask your admin/.test(text)) category = "permission";
    else if (/rate limit|slow down|too many/.test(text)) category = "rate_limit";
    else if (/billing|balance|refund|payment/.test(text)) category = "billing";
    else if (/offline|connection problem|internet|reach the server/.test(text)) category = "network";
    else if (/not found/.test(text)) category = "not_found";
    else if (/server (problem|error)|unexpected error on our side/.test(text)) category = "server";
    else if (/connect|integration|reconnect it/.test(text)) category = "integration";
    else if (/template|message|campaign|whatsapp|opted out|reply window/.test(text)) category = "messaging";
    else if (/invalid|required|check your input|too (large|long)|already exists/.test(text)) category = "validation";
    else if (/media|file|image|video|upload/.test(text)) category = "media";
    else category = "unknown";
  }

  let severity = friendly.severity;
  if (severity == null) {
    if (/restricted|banned|disabled|locked|billing issue/.test(text)) severity = "critical";
    else if (category === "rate_limit") severity = "info";
    else if (category === "validation" || category === "permission" || category === "not_found")
      severity = "warning";
    else severity = "error";
  }

  const retryable = friendly.retryable ?? RETRYABLE_TITLE_RE.test(friendly.title);

  return { ...friendly, severity, retryable, category };
}

/** "TEMPLATE_NOT_ON_META" -> "Template not on meta" — readable fallback for any code. */
function humanizeCode(code: string): string {
  const words = code
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** True when a string looks like a raw code rather than human prose. */
function looksLikeCode(s: string): boolean {
  return /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/.test(s.trim());
}

/** Generic "you can't fix this yourself" copy, used when an entry didn't author its own `whenCannotFix`. */
function defaultAskAdmin(category?: ErrorCategory): string {
  if (category === "integration" || category === "billing")
    return "This needs to be set up by an admin. Ask an admin on your team to take care of it.";
  return "You don't have permission to do this. Ask an admin on your team for help.";
}

/**
 * Tailor a resolved error to the viewer's role. Only entries marked
 * `fixableBy: "admin"` are affected: privileged viewers (owner/super-admin/admin)
 * keep the real cause + CTA; agents/anonymous get an "ask your admin" message and
 * the CTA is removed (a button they can't use is worse than no button).
 * No-ops for every other entry, so untagged errors behave exactly as before.
 */
function applyAudience(friendly: FriendlyError, audience: Audience | undefined): FriendlyError {
  if (!audience || friendly.fixableBy !== "admin") return friendly;
  const privileged = audience === "owner" || audience === "super_admin" || audience === "admin";
  if (privileged) return friendly;
  return {
    ...friendly,
    description: friendly.whenCannotFix ?? defaultAskAdmin(friendly.category),
    action: undefined,
  };
}

/**
 * Resolve a friendly title/description from any apiClient result, Error, or string.
 * Always returns something usable — never throws.
 */
/**
 * Detects raw developer/runtime errors that read like prose but mean nothing
 * to a user: JS TypeErrors, Node syscall failures, SQL driver codes, stack
 * frames. When the last-resort tier hits one of these, it shows the caller's
 * generic fallback instead of leaking internals to the toast.
 */
const DEV_JARGON_RE =
  /cannot read propert|is not a function|is not defined|undefined is not|null is not an object|\b(Type|Reference|Syntax|Range)Error\b|ECONN(REFUSED|RESET)|ETIMEDOUT|ENOTFOUND|EPIPE|socket hang up|getaddrinfo|\bER_[A-Z_]+\b|SQLSTATE|deadlock found|foreign key constraint|unexpected token|JSON at position|at \S+ \(.+:\d+:\d+\)|maximum call stack|heap out of memory/i;

export function resolveFriendlyError(
  input: ApiErrorLike | Error | string | null | undefined,
  fallback?: Partial<FriendlyError>,
  audience?: Audience,
): FriendlyError & { raw: string | null } {
  const fb: FriendlyError = {
    title: fallback?.title || "Something went wrong",
    description:
      fallback?.description || "We couldn't complete that action. Please try again.",
  };

  if (input == null) return { ...classify(fb), raw: null };

  // Normalize to a common bag of fields.
  let code: string | undefined;
  let metaCode: string | undefined;
  let rawMessage: string | undefined;
  let numericCode: number | null = null;
  let numericSubcode: number | null = null;
  let statusCode: number | null = null;

  // axios errors nest the useful bits under error.response.{data,status} — pull
  // them so axios-based callers (scheduling, etc.) keep their specific message
  // instead of falling through to the generic fallback.
  const axiosOf = (o: unknown) => {
    const resp = (o as Record<string, unknown>)?.["response"] as Record<string, unknown> | undefined;
    const data = resp?.["data"] as Record<string, unknown> | undefined;
    return {
      error: typeof data?.["error"] === "string" ? (data["error"] as string) : undefined,
      message: typeof data?.["message"] === "string" ? (data["message"] as string) : undefined,
      details: typeof data?.["details"] === "string" ? (data["details"] as string) : undefined,
      code: data?.["code"],
      status: toNum(resp?.["status"]),
    };
  };

  // The backend's most-specific reason often lives in `details` (e.g. a generic
  // error "Failed to create template" + details "HEADER component … requires
  // 'text' field"). Capture it so pattern-matching + the prose fallback can use it.
  let detailsText: string | undefined;

  if (typeof input === "string") {
    rawMessage = input;
  } else if (input instanceof Error) {
    const e = input as Error & Record<string, unknown>;
    const ax = axiosOf(e);
    rawMessage = ax.error ?? ax.message ?? input.message;
    detailsText = typeof e.details === "string" ? (e.details as string) : ax.details;
    code = typeof e.code === "string" ? (e.code as string) : typeof ax.code === "string" ? (ax.code as string) : undefined;
    numericCode = toNum(e.code) ?? toNum(e.metaCode) ?? toNum(ax.code);
    numericSubcode = toNum(e.error_subcode) ?? toNum(e.metaErrorSubcode) ?? toNum(e.metaSubcode);
    statusCode = toNum(e.statusCode) ?? toNum(e.status) ?? ax.status;
  } else {
    const bag = input as Record<string, unknown>;
    const ax = axiosOf(bag);
    // `code` may be a string machine code ("RATE_LIMITED") OR a numeric Meta code.
    code = typeof input.code === "string" ? input.code : typeof ax.code === "string" ? ax.code : undefined;
    metaCode = typeof input.meta?.code === "string" ? input.meta.code : undefined;
    rawMessage = input.error || input.message || ax.error || ax.message || undefined;
    detailsText = typeof input.details === "string" ? input.details : ax.details;
    numericCode = toNum(input.meta?.code) ?? toNum(input.code) ?? toNum(ax.code);
    numericSubcode = toNum(input.meta?.subcode) ?? toNum(bag.error_subcode);
    statusCode = toNum(input.statusCode) ?? toNum(bag.status) ?? ax.status;
  }

  // Attach metadata + the raw string to whatever entry we matched, then tailor
  // the copy/CTA to the viewer's role (no-op unless the entry is `fixableBy: "admin"`).
  const done = (friendly: FriendlyError, raw: string | null) => ({
    ...applyAudience(classify(friendly), audience),
    raw,
  });

  // 1 + 2: string-code catalog (meta.code wins over top-level code).
  for (const c of [metaCode, code]) {
    if (c && CATALOG[c]) return done(CATALOG[c], rawMessage ?? c);
  }

  // 2b: numeric Meta code/subcode (subcode is more specific, so it wins).
  // Also try a "(#12345)" code embedded in the raw Meta message string.
  const codeFromText = extractMetaCodeFromText(rawMessage);
  if (numericSubcode != null && META_SUBCODE_MAP[numericSubcode])
    return done(META_SUBCODE_MAP[numericSubcode], rawMessage ?? `#${numericSubcode}`);
  for (const nc of [numericCode, codeFromText]) {
    if (nc != null && META_CODE_MAP[nc]) return done(META_CODE_MAP[nc], rawMessage ?? `#${nc}`);
  }

  // 3: pattern match. Search both the message AND `details` — the backend's
  // most-specific reason is often only in details (generic error + rich details).
  const searchText = [rawMessage, detailsText].filter(Boolean).join(" ");
  if (searchText) {
    for (const { test, friendly } of PATTERN_MATCHERS) {
      if (test.test(searchText)) {
        if (friendly === CATALOG.NETWORK_ERROR && isOffline()) return done(CATALOG.OFFLINE, searchText);
        return done(friendly, detailsText ?? rawMessage ?? null);
      }
    }
  }

  // 4: code-shape inference for an unmapped SCREAMING_SNAKE code (de-jargons the long tail).
  const shapeCode = metaCode || code || (rawMessage && looksLikeCode(rawMessage) ? rawMessage : undefined);
  if (shapeCode) {
    const inferred = inferFromCodeShape(shapeCode);
    if (inferred) return done(inferred, rawMessage ?? shapeCode);
  }

  // 5: HTTP status fallback. 5xx / 401 / 403 / 429 win over raw text (usually technical);
  // 4xx defer to a human message when the backend supplied one.
  if (statusCode != null && HTTP_STATUS_MAP[statusCode]) {
    const serverish = statusCode >= 500 || statusCode === 401 || statusCode === 403 || statusCode === 429;
    const hasHumanProse = !!(rawMessage && rawMessage.trim() && !looksLikeCode(rawMessage));
    if (serverish || !hasHumanProse) return done(HTTP_STATUS_MAP[statusCode], rawMessage ?? `HTTP ${statusCode}`);
  }

  // 6: generic humanizer for an unknown code.
  if (shapeCode) {
    const humanized = humanizeCode(shapeCode);
    if (humanized) return done({ title: fb.title, description: `${humanized}.` }, rawMessage ?? shapeCode);
  }

  // 7: last resort — show a human-readable backend message. Prefer `details`
  // (the specific reason) over a generic `error` like "Failed to create template",
  // so the user sees what actually went wrong instead of a vague catch-all.
  const isProse = (s?: string) => !!(s && s.trim() && !looksLikeCode(s));
  const proseCandidate = isProse(detailsText) ? detailsText : isProse(rawMessage) ? rawMessage : null;
  if (proseCandidate) {
    // Developer-jargon guard: backend catch blocks sometimes forward raw JS /
    // driver errors ("Cannot read properties of null (reading 'page_id')",
    // "connect ECONNREFUSED", SQL codes). Those read as prose but mean nothing
    // to a user — show the generic fallback instead of leaking internals.
    if (DEV_JARGON_RE.test(proseCandidate)) return done(fb, proseCandidate);
    // Defensive cleanup: a backend that did `${error.message}` where message
    // was undefined/null leaks "...: undefined" / "...: null". Strip that tail
    // so the user never sees the word. If stripping leaves nothing useful,
    // fall through to the generic fallback below.
    const cleaned = proseCandidate.replace(/[:\-\s]+(undefined|null)\s*$/i, "").trim();
    if (cleaned) return done({ title: fb.title, description: cleaned }, proseCandidate);
  }

  return done(fb, rawMessage ?? null);
}
